import React, { useEffect, useState } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { ChefHat, ArrowRight } from 'lucide-react';

import Hero from '../components/Hero';
import StickyScrollExperience from '../components/StickyScrollExperience';
import Features from '../components/Features';
import VisualDemo from '../components/VisualDemo';
import Integrations from '../components/Integrations';
import Benefits from '../components/Benefits';
import Pricing from '../components/Pricing';
import CTASection from '../components/CTASection';
import Footer from '../components/Footer';
import LeadModal from '../components/LeadModal';
import WhatsAppButton from '../components/WhatsAppButton';

const LandingPage: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const [scrolled, setScrolled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('Agendar uma demonstração');

  const openDemoModal = (title?: string) => {
    if (title) setModalTitle(title);
    setModalOpen(true);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background text-white selection:bg-primary selection:text-white">
      <motion.div className="fixed left-0 right-0 top-0 z-[100] h-1 origin-left bg-primary" style={{ scaleX }} />

      <header
        className={`fixed left-0 right-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? 'border-b border-white/10 bg-background/80 py-3 backdrop-blur-md' : 'bg-transparent py-4 md:py-6'
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
          <div
            className="group flex cursor-pointer items-center gap-2"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary shadow-glow-sm transition-transform group-hover:scale-110">
              <ChefHat className="h-5 w-5 text-white" />
            </div>
            <span className="text-base font-black tracking-tighter sm:text-xl">Garcom Digital</span>
          </div>

          <nav className="hidden items-center gap-8 md:flex">
            <a href="#recursos" className="text-sm font-medium text-muted-foreground transition-colors hover:text-white">
              Recursos
            </a>
            <a href="#integracoes" className="text-sm font-medium text-muted-foreground transition-colors hover:text-white">
              Integracoes
            </a>
            <a href="#precos" className="text-sm font-medium text-muted-foreground transition-colors hover:text-white">
              Precos
            </a>
            <a href="#beneficios" className="text-sm font-medium text-muted-foreground transition-colors hover:text-white">
              Beneficios
            </a>
          </nav>

          <div className="flex shrink-0 items-center gap-2 sm:gap-4">
            <a href="/admin/login" className="text-sm font-bold text-white transition-colors hover:text-primary">
              Entrar
            </a>
            <a
              href="/admin/login"
              className="btn-primary flex items-center gap-1.5 rounded-full px-3 py-2 text-xs sm:gap-2 sm:px-5 sm:text-sm"
            >
              <span className="hidden sm:inline">Comecar Gratis</span>
              <span className="sm:hidden">Gratis</span>
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </header>

      <main>
        <Hero onOpenDemo={openDemoModal} />

        <div id="experience">
          <StickyScrollExperience />
        </div>

        <div id="recursos">
          <Features onOpenDemo={openDemoModal} />
        </div>

        <div id="demo">
          <VisualDemo />
        </div>

        <div id="integracoes">
          <Integrations />
        </div>

        <div id="beneficios">
          <Benefits />
        </div>

        <div id="precos">
          <Pricing onOpenDemo={openDemoModal} />
        </div>

        <div id="cta">
          <CTASection onOpenDemo={openDemoModal} />
        </div>
      </main>

      <Footer />

      <LeadModal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        title={modalTitle} 
      />
      <WhatsAppButton />
    </div>
  );
};

export default LandingPage;
